import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import type {
  Notification,
  NotificationTone,
} from '@/services/notifications/types';

/**
 * Destino do clique, na forma que o TanStack Router espera.
 *
 * Estruturado e não uma URL montada à mão: o `policyId` do InsureMO tem
 * vírgula, e a aba viaja em `search` — passar tudo concatenado em `to` faria o
 * router tentar casar a query string como parte do caminho.
 */
export type NotificationTarget = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
};

export type NotificationPresenter = {
  icon: LucideIcon;
  /** Destino do clique. `undefined` = notificação sem navegação. */
  buildTarget?: (
    data: Record<string, unknown>
  ) => NotificationTarget | undefined;
};

function readString(
  data: Record<string, unknown>,
  key: string
): string | undefined {
  const value = data[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Registry de apresentação por tipo de notificação.
 *
 * Mesma ideia do `products.config.ts`: o que varia por tipo é DADO, resolvido
 * aqui e entregue pronto ao componente. Nenhum `.tsx` pergunta qual é o tipo.
 *
 * Deliberadamente parcial: tipo sem entrada cai no fallback abaixo e ainda
 * aparece no sino e na tela, porque título, mensagem e tom vêm da API. Um tipo
 * novo no back só precisa de entrada aqui quando quiser ícone ou link próprios.
 */
export const NOTIFICATION_PRESENTERS: Record<string, NotificationPresenter> = {
  POLICY_EMISSION_SUCCEEDED: {
    icon: CheckCircle2,
    // Direto na aba Cobrança: é onde está o link de pagamento que o corretor
    // precisa copiar — o motivo de o aviso existir.
    buildTarget: data => {
      const policyId = readString(data, 'insuremoPolicyId');
      return policyId
        ? {
            to: '/policies/$policyId',
            params: { policyId },
            search: { tab: 'billing' },
          }
        : undefined;
    },
  },
  POLICY_EMISSION_FAILED: {
    icon: AlertTriangle,
    buildTarget: data => {
      const policyId = readString(data, 'insuremoPolicyId');
      return policyId
        ? { to: '/policies/$policyId', params: { policyId } }
        : undefined;
    },
  },
  // O segurado autorizou o PIX Automático: leva à aba Cobrança, onde o mandato
  // aparece como autorizado e as parcelas como "No PIX Automático".
  POLICY_PAYMENT_CHARGE_AUTHORIZED: {
    icon: CheckCircle2,
    buildTarget: data => {
      const policyId = readString(data, 'insuremoPolicyId');
      return policyId
        ? {
            to: '/policies/$policyId',
            params: { policyId },
            search: { tab: 'billing' },
          }
        : undefined;
    },
  },
  // Apólice emitida, link não. Leva à aba Cobrança, que é onde está o botão de
  // tentar de novo — o aviso existe justamente para essa ação.
  POLICY_PAYMENT_CHARGE_FAILED: {
    icon: AlertTriangle,
    buildTarget: data => {
      const policyId = readString(data, 'insuremoPolicyId');
      return policyId
        ? {
            to: '/policies/$policyId',
            params: { policyId },
            search: { tab: 'billing' },
          }
        : undefined;
    },
  },
};

const FALLBACK_PRESENTER: NotificationPresenter = { icon: Bell };

export function getNotificationPresenter(type: string): NotificationPresenter {
  return NOTIFICATION_PRESENTERS[type] ?? FALLBACK_PRESENTER;
}

/** O que os componentes de notificação recebem — nada além disso. */
export type NotificationViewModel = {
  id: string;
  type: string;
  icon: LucideIcon;
  tone: NotificationTone;
  title: string;
  description: string;
  /** Data da notificação, já formatada. */
  createdAtLabel: string;
  /** Data de leitura já formatada, ou `undefined` enquanto não lida. */
  readAtLabel?: string;
  createdAt: string;
  readAt: string | null;
  read: boolean;
  target?: NotificationTarget;
};

export function toNotificationViewModel(
  notification: Notification,
  formatDate: (value: string | null | undefined) => string
): NotificationViewModel {
  const presenter = getNotificationPresenter(notification.type);
  const data = notification.data ?? {};

  return {
    id: notification.id,
    type: notification.type,
    icon: presenter.icon,
    tone: notification.tone,
    title: notification.title,
    description: notification.message,
    createdAtLabel: formatDate(notification.createdAt),
    readAtLabel: notification.readAt
      ? formatDate(notification.readAt)
      : undefined,
    createdAt: notification.createdAt,
    readAt: notification.readAt,
    // "Lida" sai de `readAt` e de mais nada — uma fonte só para os dois campos.
    read: notification.readAt != null,
    target: presenter.buildTarget?.(data),
  };
}
